import { LitElement, html } from 'lit';
import { repeat } from 'lit/directives/repeat';
import { unsafeHTML } from 'lit/directives/unsafe-html';
import '../fit-toast/fit-toast';
import '../fit-toaster/fit-toaster';
import './fit-search-result';
import scrollMonitor from 'scrollMonitor';
import { connect } from 'pwa-helpers';
import { translate, pluralize } from '../fit-polyglot/fit-polyglot-behavior';
import { WizardLinkToBehavior } from '../fit-wizard/fit-wizard-link-to-behavior';
import styles from './fit-search.css';
import { store } from '../fit-wizard/configureStore';
import { confirmWizardSelection, resetWizardSelection } from '../fit-wizard/actions';

let elementWatcher;

function hostOnly(ibeUrl) {
  const url = (new URL(ibeUrl));
  return `${url.protocol}//${url.host}`;
}

class FitSearch extends connect(store)(WizardLinkToBehavior(LitElement)) {
  static get is() { return 'fit-search'; }

  static get styles() { return styles; }

  render() {
    return html`
    <div id="results">

      <div class="top-shortages">
      ${(this.shortages.RootResultsTop || []).map((shortage) => unsafeHTML(shortage.Message))}
      </div>

      ${repeat(this.items, (hotel) => hotel.Code, (hotel) => html`
        <fit-search-result class="result" .hotel="${hotel}"
          base-url="${hostOnly(this.endpointUrl)}" .additional-params="${this.additionalParams}"
          .labels="${this.labels}" unresolved></fit-search-result>
      `)}

      <div class="loading" ?hidden="${this.allShown}">
        <fit-spinner ?hidden="${!this.loading}"></fit-spinner>
      </div>

      <div id="no-results" ?hidden="${!(this.loading === false && this.total === 0)}" @click="${this.resetSearch}">
        <slot name="no-results"></slot>
      </div>

      <div class="more-results grid">
        <div class="grid__column grid__column--single">
          <button ?hidden="${!this.moreToShow}" id="more-results-trigger" class="button button--expanded button--enlarged" @click="${this.redirectToSerp}">${pluralize(this.labels.MoreResults, this.total)}</button>
          <button ?hidden="${!this.nextToShow}" id="next-page-trigger" class="button button--expanded button--enlarged" @click="${this.loadNextPage}">${translate(this.labels.NextPage)}</button>
        </div>
      </div>

      <fit-toaster class="toasts">
        ${this.shortages.ThinkToTravel ? html`
          <fit-toast horizontal-align="right" duration="10000" class="shard shard--info shard--shallow">
            <span class="shard__fragment fa fa-users"></span>
            <p class="shard__fragment">${this.shortages.ThinkToTravel}</p>
          </fit-toast>
        ` : ''}
      </fit-toaster>
    </div>`;
  }

  static get properties() {
    return {
      endpointUrl: {
        type: String,
        attribute: 'endpoint-url',
      },

      ibeUrl: {
        type: String,
        attribute: 'ibe-url',
      },

      page: {
        type: Number,
      },

      loading: {
        type: Boolean,
      },

      items: {
        type: Array,
      },

      shortages: {
        type: Object,
      },

      total: {
        type: Number,
      },

      infiniteScroll: {
        type: Boolean,
        attribute: 'infinite-scroll',
      },

      nextPageTrigger: {
        type: Boolean,
        attribute: 'next-page-trigger',
      },

      labels: {
        type: Object, notify: true,
      },
    };
  }

  constructor(props) {
    super(props);
    this._page = this.total = 0;
    this.loading = true;
    this.items = [];
    this.shortages = [];
    this.labels = {
      OpenProgram: '',
      CloseProgram: '',
      CtaProgram: '',
      RatingCount: '',
      MoreResults: '',
      NextPage: '',
    };
    this.nextPageTrigger = this.infiniteScroll = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._storeUnsubscribe && this._storeUnsubscribe() && (this._storeUnsubscribe = null);
    this._storeUnsubscribe = store.subscribe(() => this.onStateChange(store.getState()));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  firstUpdated(changedProperties) {
    this.$ = {};
    this.$.results = this.shadowRoot.querySelector('#results');
    this.infiniteScroll = this.infiniteScroll;
  }

  set parentWizard(wizard) {
    this._parentWizard = wizard;
  }

  onStateChange(state) {
    if (typeof state.ibeUrl === 'string' && state.ibeUrl !== this.ibeUrl) {
      this.ibeUrl = state.ibeUrl;
    }
  }

  set ibeUrl(ibeUrl) {
    this._ibeUrl = ibeUrl;
    this._page = 1;
    this.items = [];
    this.fetchResults();
  }

  get ibeUrl() {
    return this._ibeUrl;
  }

  set page(page) {
    this._page = page;
    this.fetchResults();
  }

  get page() {
    return this._page;
  }

  get additionalParams() {
    return '';
    // TODO implement whitelabel arg extraction
  }

  get allShown() {
    return this.loading === false && (this.total === 0 || this.items.length >= this.total || !this.infiniteScroll);
  }

  get moreToShow() {
    return this.nextPageTrigger && !this.loading && (this.items.length < this.total);
  }

  get nextToShow() {
    return !this.loading && (this.items.length < this.total);
  }

  set infiniteScroll(infiniteScroll) {
    this._infiniteScroll = infiniteScroll;
    if (!infiniteScroll) {
      if (elementWatcher) {
        elementWatcher.destroy();
        elementWatcher = null;
      }
      return;
    }

    this.setUpScrollWatcher();
  }

  get infiniteScroll() {
    return this._infiniteScroll;
  }

  setUpScrollWatcher() {
    if (!this.$) {
      return;
    }

    const renderedItemCount = this.$.results.querySelectorAll('.result').length; // because this.$.resultRenderer.renderedItemCount is unreliable on iOS Safari
    // this.items.length = renderedItemCount
    if (!renderedItemCount || !this.items.length
      || (renderedItemCount && this.items.length !== renderedItemCount)) {
      return;
    }

    if (elementWatcher) {
      elementWatcher.destroy();
      elementWatcher = null;
    }
    if (this.allShown) {
      return;
    }

    const results = this.$.results.querySelectorAll('.result');
    const nthLastChild = results[results.length - 1];

    if (nthLastChild) {
      const registerWatcher = () => {
        elementWatcher = scrollMonitor.create(nthLastChild);
        elementWatcher.one('preEnterViewport', () => {
          this.page++;
        });
      };
      if (nthLastChild.hasAttribute('unresolved')) {
        const resolvedListener = () => {
          nthLastChild.removeEventListener('fit-resolved', resolvedListener);
          setTimeout(registerWatcher);
        };
        nthLastChild.addEventListener('fit-resolved', resolvedListener);
      } else {
        registerWatcher();
      }
    }
  }

  redirectToSerp(event) {
    this._parentWizard && this._parentWizard.redirectToSerp(event);
  }

  resetSearch(event) {
    if (event.target.hasAttribute('rel')
      && event.target.getAttribute('rel') === 'fit-search-reset'
      && this._parentWizard
    ) {
      store.dispatch(resetWizardSelection());
      store.dispatch(confirmWizardSelection());
      event.preventDefault();
    }
  }

  fetchResults() {
    if (this._activeFetch) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const resultsUrl = `${this.endpointUrl
    }?${this._ibeUrl
    }&results=${this.infiniteScroll ? 15 : 7
    }&page=${this._page
    }&${this.additionalParams}`;

    this.loading = true;
    return this._activeFetch = fetch(resultsUrl, { signal: this.abortController.signal })
      .then((response) => response.json())
      .then((json) => this.handleJsonResponse(json))
      .then(() => {
        this._activeFetch = null;
        this.loading = false;
      })
      .catch(e => {
        if (e.name === 'AbortError') {
          return;
        }
        this._activeFetch = null;
        this.loading = false;
      });

  }

  handleJsonResponse(response) {
    this.total = response.Results;
    if (this._page > 1) {
      this.items = this.items.concat(response.Hotels);
    } else {
      this.shortages = this.filterAndIndexShortages(response);
      this.items = response.Hotels;
      this.dispatchEvent(new CustomEvent('fit-search-first-page', { detail: this.items, bubbles: false, composed: true }));
    }
    this.updateComplete.then(() => {
      this.infiniteScroll && this.setUpScrollWatcher();
    });
  }

  filterAndIndexShortages(response) {
    const shortages = {};
    if (response.Info !== undefined) {
      response.Info.forEach((info) => {
        if (info.Position === undefined || info.Shortages === undefined) {
          return;
        }

        shortages[info.Position] = (shortages[info.Position] || []).concat(info.Shortages);
      });
    }
    if (this.infiniteScroll && response.DestinationThinkToTravelMessage !== undefined) {
      shortages.ThinkToTravel = response.DestinationThinkToTravelMessage;
    }
    return shortages;
  }

  loadNextPage() {
    this.page++;
  }
}

customElements.define(FitSearch.is, FitSearch);
